<script setup lang="ts">
import Tag from "~/components/Common/Tag.vue";
import StatusTag from "~/components/Common/StatusTag.vue";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { CopilotFieldStateEnum } from "~/utils/enums/copilot/workflow.enums";
import { attendanceRecapCounts, isAttendanceRecap } from "~/utils/helpers/copilot/recap";
import type { ICopilotFieldsRecap, ICopilotRecap } from "~/utils/interfaces/copilot-blocks-interfaces";

const props = defineProps<{
  recap: ICopilotRecap;
}>();

const FIELD_STATE_TAG_TYPE: Record<CopilotFieldStateEnum, ColorsTypeEnum> = {
  [CopilotFieldStateEnum.EXTRACTED]: ColorsTypeEnum.LIGHT_PURPLE,
  [CopilotFieldStateEnum.MISSING]: ColorsTypeEnum.LIGHT_ORANGE,
};

const TO_COMPLETE_LABEL = "à compléter";

/**
 * The attendance recap counts the buckets of a proposal; the training import lists the fields it
 * read. They share the bordered card and nothing else, so each gets its own branch rather than a
 * single template making every field optional — which is what rendered an empty attendance bubble.
 */
const counts = computed(() => (isAttendanceRecap(props.recap) ? attendanceRecapCounts(props.recap) : null));
const fieldsRecap = computed(() => (isAttendanceRecap(props.recap) ? null : (props.recap as ICopilotFieldsRecap)));
</script>

<template>
  <div v-if="counts" class="flex flex-col rounded-lg border border-stroke-color bg-light-color">
    <div
      v-for="line of counts"
      :key="line.label"
      class="flex items-center justify-between gap-3 px-4 py-2 border-b border-stroke-color last:border-b-0"
    >
      <span class="body_regular text-secondary-color">{{ line.label }}</span>
      <span class="body_med text-primary-color shrink-0">{{ line.value }}</span>
    </div>
  </div>

  <div v-else-if="fieldsRecap" class="flex flex-col gap-3">
    <span v-if="fieldsRecap.intro" class="body_regular text-primary-color">{{ fieldsRecap.intro }}</span>

    <!-- Guarded: an empty container still draws its border, which reads as a stray line in the bubble. -->
    <div v-if="fieldsRecap.fields?.length" class="flex flex-col rounded-lg border border-stroke-color bg-light-color">
      <div
        v-for="field of fieldsRecap.fields"
        :key="field.label"
        class="flex items-center justify-between gap-3 px-4 py-2 border-b border-stroke-color last:border-b-0"
      >
        <span class="body_regular text-secondary-color shrink-0">{{ field.label }}</span>
        <div class="flex items-center gap-2 min-w-0 justify-end">
          <StatusTag
            v-if="field.state === CopilotFieldStateEnum.MISSING"
            :tag="{ value: TO_COMPLETE_LABEL, type: FIELD_STATE_TAG_TYPE[field.state] }"
          />
          <template v-else>
            <span class="body_med text-primary-color truncate">{{ field.value }}</span>
            <Tag v-if="field.badge" :tag="{ value: field.badge, type: FIELD_STATE_TAG_TYPE[field.state] }" />
          </template>
        </div>
      </div>
    </div>

    <div
      v-for="section of fieldsRecap.sections"
      :key="section.label"
      class="flex flex-col gap-1 rounded-lg border border-stroke-color bg-light-color px-4 py-2"
    >
      <span class="body_regular text-secondary-color">{{ section.label }}</span>
      <span class="body_regular text-primary-color">{{ section.content }}</span>
    </div>

    <span v-if="fieldsRecap.outro" class="body_regular text-primary-color">{{ fieldsRecap.outro }}</span>
  </div>
</template>
